// components/secure/pm/PMAgreementsPanel.js
// Property Management → Clients → an owner → Agreements.
//
// The management agreement is the gate for everything else in the module (no executed
// agreement, no rent receipt), and until this panel it could be created, completed and
// executed only through the API. Here: draft one, complete it and its fees, preview the
// merged document, execute it (managing broker), and store the executed copy.

import React, { useCallback, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Panel, Table, Modal, Input, Select, Textarea, Button, Notice, Pill, fmtDate, titleCase, statusTone, money } from './PMKit';

const AUTHORITY = [
    { value: 'execute_leases', label: 'May sign leases' },
    { value: 'negotiate_only', label: 'May negotiate, owner signs' },
    { value: 'none', label: 'No leasing authority' },
];
const DRAWS = ['monthly', 'quarterly', 'on_request'];
const EDITABLE = ['agreement_no', 'effective_date', 'expiration_date', 'auto_renew', 'termination_notice_days', 'scope_notes',
    'leasing_authority', 'spend_limit_without_approval', 'emergency_spend_limit', 'owner_draw_frequency', 'owner_draw_day', 'reserve_floor'];

function AgreementFields({ a, set }) {
    const f = (k) => (e) => set({ ...a, [k]: e.target.value });
    return (
        <div className="grid sm:grid-cols-2 gap-x-4">
            <Input label="Agreement no." value={a.agreement_no || ''} onChange={f('agreement_no')} />
            <Input label="Termination notice (days)" type="number" value={a.termination_notice_days ?? ''} onChange={f('termination_notice_days')} />
            <Input label="Effective date" type="date" value={a.effective_date || ''} onChange={f('effective_date')} hint="Required before execution." />
            <Input label="Expires" type="date" value={a.expiration_date || ''} onChange={f('expiration_date')} />
            <Select label="Leasing authority" value={a.leasing_authority || 'execute_leases'} options={AUTHORITY} onChange={f('leasing_authority')} />
            <label className="flex items-center gap-2 text-sm mb-3 mt-5"><input type="checkbox" checked={!!Number(a.auto_renew)} onChange={(e) => set({ ...a, auto_renew: e.target.checked ? 1 : 0 })} /> Renews automatically</label>
            <Input label="Spend without owner approval ($)" type="number" value={a.spend_limit_without_approval ?? ''} onChange={f('spend_limit_without_approval')} />
            <Input label="Emergency spend limit ($)" type="number" value={a.emergency_spend_limit ?? ''} onChange={f('emergency_spend_limit')} />
            <Select label="Owner draws" value={a.owner_draw_frequency || 'monthly'} options={DRAWS} onChange={f('owner_draw_frequency')} />
            <Input label="Draw day of month" type="number" min="1" max="28" value={a.owner_draw_day ?? ''} onChange={f('owner_draw_day')} />
            <Input label="Reserve floor ($)" type="number" value={a.reserve_floor ?? ''} onChange={f('reserve_floor')} />
            <Textarea label="Scope notes" value={a.scope_notes || ''} onChange={f('scope_notes')} />
        </div>
    );
}

export default function PMAgreementsPanel({ owner, onChanged, isBroker }) {
    const api = useApiClient();
    const [creating, setCreating] = useState(null);
    const [open, setOpen] = useState(null);     // the full agreement being viewed
    const [doc, setDoc] = useState(null);       // { html, unresolved_fields }
    const [fee, setFee] = useState(null);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    const openAgreement = useCallback(async (id) => {
        setError(null); setDoc(null);
        try { setOpen(await api.get(`/api/property-management/agreements/${id}`)); } catch (e) { setError({ message: e.message }); }
    }, [api]);

    const act = async (fn, after) => {
        setBusy(true); setError(null); setMsg('');
        try { const r = await fn(); if (r?.message) setMsg(r.message); if (after) await after(r); if (onChanged) await onChanged(); return true; }
        catch (e) { setError({ message: e.message, problems: e.problems || e.payload?.unresolved_fields }); return false; }
        finally { setBusy(false); }
    };

    const create = () => act(() => api.post('/api/property-management/agreements', {
        ...creating, owner_id: owner.owner_id, pm_property_id: creating.pm_property_id || null,
    }), async (r) => { setCreating(null); setMsg(`Draft created with ${r.fee_items_copied} fee line(s) from the office template.`); await openAgreement(r.agreement_id); });

    const saveDraft = () => {
        const body = {};
        for (const k of EDITABLE) body[k] = open[k] === undefined ? null : open[k];
        return act(() => api.put(`/api/property-management/agreements/${open.agreement_id}`, body), () => openAgreement(open.agreement_id));
    };
    const execute = () => {
        if (!window.confirm('Execute this agreement?\n\nOnly once the owner has signed it. Rent may then be received on the properties it covers (68 IAC 1450.750).')) return;
        act(() => api.post(`/api/property-management/agreements/${open.agreement_id}/execute`, {}), () => openAgreement(open.agreement_id));
    };
    const preview = () => act(async () => setDoc(await api.get(`/api/property-management/agreements/${open.agreement_id}/document`)));
    const store = () => act(() => api.post(`/api/property-management/agreements/${open.agreement_id}/document`, {}),
        async () => { setMsg('Executed copy stored with the owner\'s documents.'); await openAgreement(open.agreement_id); });
    const saveFee = () => {
        const body = {};
        for (const k of ['label', 'rate', 'amount', 'minimum_amount', 'maximum_amount', 'notes']) body[k] = fee[k] === '' ? null : fee[k];
        body.is_active = fee.is_active ? 1 : 0;
        return act(() => api.put(`/api/property-management/fee-items/${fee.fee_item_id}`, body), async () => { setFee(null); await openAgreement(open.agreement_id); });
    };

    const props = owner.properties || [];
    return (
        <Panel title="Management agreements"
            subtitle="No agreement, no work. Rent is outside escrow only where a written agreement governs its transmittal."
            actions={<Button variant="ghost" onClick={() => setCreating({ agreement_type: 'multifamily_residential', pm_property_id: '', leasing_authority: 'execute_leases', owner_draw_frequency: 'monthly', owner_draw_day: 15, termination_notice_days: 30 })}>+ New agreement</Button>}>
            {error && <Notice tone="bad" message={error.message} problems={error.problems} onDismiss={() => setError(null)} />}
            {msg && <Notice tone="good" message={msg} onDismiss={() => setMsg('')} />}
            <Table
                columns={[
                    { key: 'agreement_no', label: 'No.', nowrap: true, render: (r) => r.agreement_no || `#${r.agreement_id}` },
                    { key: 'agreement_type', label: 'Form', render: (r) => titleCase(r.agreement_type) },
                    { key: 'effective_date', label: 'Effective', nowrap: true, render: (r) => fmtDate(r.effective_date) },
                    { key: 'expiration_date', label: 'Expires', nowrap: true, render: (r) => fmtDate(r.expiration_date) },
                    { key: 'status', label: 'Status', nowrap: true, render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                ]}
                rows={(owner.agreements || []).map((a) => ({ ...a, _key: a.agreement_id }))}
                onRowClick={(r) => openAgreement(r.agreement_id)}
                empty="No agreements. Nothing may be done on this client's properties until one is executed."
            />

            {creating && (
                <Modal title={`New management agreement — ${owner.legal_name}`} subtitle="Starts as a draft with the office fee template copied in." onClose={() => setCreating(null)} wide>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Select label="Form" value={creating.agreement_type} onChange={(e) => setCreating({ ...creating, agreement_type: e.target.value })}
                            options={[{ value: 'multifamily_residential', label: 'Multifamily residential (5+ units)' }, { value: 'commercial', label: 'Commercial' }]} />
                        <Select label="Covers" value={creating.pm_property_id} onChange={(e) => setCreating({ ...creating, pm_property_id: e.target.value })}
                            options={[{ value: '', label: 'All of this owner\'s properties' }, ...props.map((p) => ({ value: p.pm_property_id, label: `${p.name} (${p.total_units} units, ${p.asset_class})` }))]} />
                    </div>
                    <AgreementFields a={creating} set={setCreating} />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setCreating(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={create}>Create draft</Button>
                    </div>
                </Modal>
            )}

            {open && (
                <Modal title={`Agreement ${open.agreement_no || `#${open.agreement_id}`} — ${titleCase(open.agreement_type)}`}
                    subtitle={`Status: ${titleCase(open.status)}${open.executed_on ? ` · executed ${fmtDate(open.executed_on)}` : ''}${open.document_path ? ' · executed copy stored' : ''}`}
                    onClose={() => { setOpen(null); setDoc(null); }} wide>
                    {open.status === 'draft'
                        ? <AgreementFields a={open} set={setOpen} />
                        : <p className="text-sm text-slate-600 mb-3">An executed agreement is what the owner signed. To change it, make a new agreement.</p>}
                    <h4 className="text-sm font-semibold text-slate-700 mt-2 mb-1">Fees</h4>
                    <Table
                        columns={[
                            { key: 'label', label: 'Fee' },
                            { key: 'basis', label: 'Basis', render: (r) => titleCase(r.basis) },
                            { key: 'rate', label: 'Rate %', align: 'right' },
                            { key: 'amount', label: 'Amount', align: 'right', render: (r) => (r.amount == null ? '—' : money(r.amount)) },
                            { key: 'is_active', label: 'In agreement', render: (r) => (r.is_active ? 'yes' : 'no') },
                        ]}
                        rows={(open.fee_items || []).map((f) => ({ ...f, _key: f.fee_item_id }))}
                        onRowClick={open.status === 'draft' ? (r) => setFee({ ...r }) : undefined}
                        empty="No fees."
                    />
                    <div className="flex flex-wrap justify-end gap-2 mt-4">
                        {open.status === 'draft' && <Button variant="ghost" disabled={busy} onClick={saveDraft}>Save draft</Button>}
                        <Button variant="ghost" disabled={busy} onClick={preview}>Preview document</Button>
                        {isBroker && open.status === 'draft' && <Button disabled={busy} onClick={execute}>Execute (owner has signed)</Button>}
                        {isBroker && open.status === 'active' && !open.document_path && <Button disabled={busy} onClick={store}>Store executed copy</Button>}
                    </div>
                    {doc && (
                        <div className="mt-4">
                            {(doc.unresolved_fields || []).length > 0 && (
                                <Notice tone="warn" title="Not complete yet" message="These could not be filled from the record:" problems={doc.unresolved_fields} />
                            )}
                            <iframe title="Agreement preview" sandbox="" srcDoc={doc.html} className="w-full h-[60vh] border border-slate-200 rounded" />
                        </div>
                    )}
                </Modal>
            )}

            {fee && (
                <Modal title={`Fee — ${fee.label}`} subtitle="This agreement only; the office template is unchanged." onClose={() => setFee(null)}>
                    <Input label="Label" value={fee.label || ''} onChange={(e) => setFee({ ...fee, label: e.target.value })} />
                    <div className="grid grid-cols-2 gap-x-4">
                        <Input label="Rate (%)" type="number" step="any" value={fee.rate ?? ''} onChange={(e) => setFee({ ...fee, rate: e.target.value })} />
                        <Input label="Amount ($)" type="number" step="any" value={fee.amount ?? ''} onChange={(e) => setFee({ ...fee, amount: e.target.value })} />
                        <Input label="Minimum ($)" type="number" step="any" value={fee.minimum_amount ?? ''} onChange={(e) => setFee({ ...fee, minimum_amount: e.target.value })} />
                        <Input label="Maximum ($)" type="number" step="any" value={fee.maximum_amount ?? ''} onChange={(e) => setFee({ ...fee, maximum_amount: e.target.value })} />
                    </div>
                    <Textarea label="Notes" value={fee.notes || ''} onChange={(e) => setFee({ ...fee, notes: e.target.value })} />
                    <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!fee.is_active} onChange={(e) => setFee({ ...fee, is_active: e.target.checked })} /> Part of this agreement</label>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setFee(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={saveFee}>Save</Button>
                    </div>
                </Modal>
            )}
        </Panel>
    );
}
